// src/components/Header.tsx
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navLinks = [
    { href: '/', label: 'Domov' },
    { href: '/o-nas', label: 'O nas' },
    { href: '/storitev', label: 'Storitve' },
    { href: '/galerija', label: 'Galerija' },
    { href: '/kontakt', label: 'Kontakt' }
  ];

  const isActiveLink = (href: string) => {
    return location.pathname === href;
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'glass-card mx-6 mt-4 rounded-3xl' : 'bg-transparent'
      }`}>
        <nav className="container mx-auto px-6 lg:px-12 py-2">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link 
              to="/" 
              onClick={scrollToTop}
              className="text-3xl font-lora font-semibold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent hover:scale-105 transition-transform duration-300 flex items-center leading-none"
              style={{ 
                lineHeight: 1,
                paddingBottom: 0,
                marginBottom: 0,
                height: 'auto'
              }}
            >
              Allmaverse
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={scrollToTop}
                  className={`relative px-3 py-1 text-base font-medium transition-all duration-300 hover:text-purple-300 flex items-center leading-none ${
                    isActiveLink(link.href) 
                      ? 'text-purple-300' 
                      : 'text-white/90'
                  }`}
                >
                  {link.label}
                  {isActiveLink(link.href) && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-linear-to-r from-purple-400 to-pink-400 rounded-full"></span>
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <Link
              to="/kontakt"
              onClick={scrollToTop}
              className="hidden md:flex glass-card px-6 py-2 text-base font-medium text-white hover:bg-white/20 transition-all duration-300 hover:scale-105 relative overflow-hidden group items-center space-x-2 leading-none h-auto"
            >
              <span className="relative z-10">Rezerviraj termin</span>
              <ArrowRight className="w-5 h-5 relative z-10" />
              <div className="absolute inset-0 bg-linear-to-r from-purple-400/20 to-pink-400/20 -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden glass-card p-3 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden glass-card mx-6 mb-4 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => {
                    setIsMenuOpen(false);
                    scrollToTop();
                  }}
                  className={`block py-2 text-base font-medium transition-colors hover:text-purple-300 ${
                    isActiveLink(link.href) 
                      ? 'text-purple-300' 
                      : 'text-white/90'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/kontakt"
                onClick={() => {
                  setIsMenuOpen(false);
                  scrollToTop();
                }}
                className="flex w-full items-center justify-center space-x-2 glass-card px-4 py-3 text-base font-medium text-white hover:bg-white/20 transition-colors rounded-xl"
              >
                <span>Rezerviraj termin</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;